import { decodeJwt } from "../../utils/jwt/decode-jwt";
import { sleep } from "../../utils/sleep";
import {
    JwtToken,
    TokenVerification,
    VerificationResponse,
} from "./gated-content-types";

const localStorageKey = "trumpf.ux.gatedContentTokens";

/**
 * new format
 */
interface LocalStorageTokensWithDate {
    [token: string]: TokenDetails;
}

interface TokenDetails {
    date: string;
    migrated?: boolean; // indicates that the token was migrated from old format
}

type LocalStorageTokens = string[] | LocalStorageTokensWithDate;

/**
 * In the first iteration we only saved the token in local storage.
 * Now we save the token together with the date when it was first saved.
 * Our functions are backwards compatible.
 */

export const getLocalStorageTokens = function (): LocalStorageTokensWithDate {
    const localStorageData: LocalStorageTokens =
        localStorage.getItem(localStorageKey) !== null
            ? JSON.parse(localStorage.getItem(localStorageKey))
            : {};
    // transform old data to new format
    if (Array.isArray(localStorageData)) {
        const transformedData = transformLocalStorage(localStorageData);
        localStorage.setItem(localStorageKey, JSON.stringify(transformedData));
        return transformedData;
    }
    return localStorageData;
};

/**
 * Migrate old local storage data (string[]) to new format (LocalStorageTokensWithDate).
 */
const transformLocalStorage = function (
    oldTokens: string[]
): LocalStorageTokensWithDate {
    const newTokens: LocalStorageTokensWithDate = {};
    oldTokens.forEach((token) => {
        newTokens[token] = { date: new Date().toISOString(), migrated: true };
    });
    return newTokens;
};

export const removeFromLocalStorage = function (token: string): void {
    const tokens = getLocalStorageTokens();
    if (tokens[token]) {
        delete tokens[token];
        localStorage.setItem(localStorageKey, JSON.stringify(tokens));
    }
};

export const addToLocalStorage = function (token: string, date: string): void {
    const tokens = getLocalStorageTokens();
    if (tokens[token]) return;
    tokens[token] = { date };
    localStorage.setItem(localStorageKey, JSON.stringify(tokens));
};

export const verifyToken = async function (
    token: string,
    verifyTokenUrl: string
): Promise<TokenVerification> {
    const isMockData = verifyTokenUrl.includes("/mock-data/");
    const config = isMockData
        ? {
              method: "GET",
          }
        : {
              method: "POST",
              headers: {
                  "Content-Type": "text/plain",
              },
              body: token,
          };
    // send token to server for verification
    return fetch(verifyTokenUrl, config)
        .then(sleep(isMockData ? 1000 : 0)) // simulate network delay for storybook
        .then((response) => response.json())
        .then((response: VerificationResponse) => {
            return {
                token,
                result: response.result,
                date: new Date().toISOString(),
            };
        })
        .catch(() => {
            return {
                token,
                result: "error",
                date: new Date().toISOString(),
            };
        });
};

export const persistResultsInLocalStorage = function (
    results: TokenVerification[]
): void {
    results.forEach(({ token, result, date }) => {
        const decodedToken = decodeJwt<JwtToken>(token);
        if (result === "valid" && decodedToken.persist) {
            addToLocalStorage(token, date);
        }
        if (result === "blacklisted") {
            removeFromLocalStorage(token);
        }
    });
};
